import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const openActions = [
  { id: 1, title: 'Close interlock bypass on Unit 4 reactor', source: 'Process Safety', owner: 'R. Sharma', due: '2025-01-22', priority: 'critical', status: 'overdue' },
  { id: 2, title: 'Conduct electrical safety re-certification for 6 personnel', source: 'Compliance', owner: 'L. Chen', due: '2025-01-28', priority: 'high', status: 'in-progress' },
  { id: 3, title: 'Investigate near miss in Warehouse B — forklift proximity', source: 'Incident', owner: 'M. Patel', due: '2025-01-25', priority: 'high', status: 'open' },
  { id: 4, title: 'Review NOx emission control settings on Boiler 2', source: 'Environmental', owner: 'A. Kumar', due: '2025-02-01', priority: 'medium', status: 'open' },
  { id: 5, title: 'Update HAZMAT handling SOP for new chemical batch', source: 'Process Safety', owner: 'S. Ali', due: '2025-02-05', priority: 'medium', status: 'open' },
];

const boardColumns = [
  { id: 'open', label: 'Open', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  { id: 'in-progress', label: 'In Progress', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  { id: 'overdue', label: 'Overdue', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  { id: 'closed', label: 'Closed', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
];

const escalationAlerts = [
  { id: 1, title: 'Interlock bypass — 72hr threshold breached', module: 'Process Safety', assignee: 'Plant Manager', rag: 'red' },
  { id: 2, title: '6 expired electrical certs — personnel in restricted areas', module: 'Compliance', assignee: 'HSE Director', rag: 'red' },
  { id: 3, title: 'Near miss rate 14% above target — 2 repeat locations', module: 'Behavioural Safety', assignee: 'Safety Manager', rag: 'amber' },
];

const priorityBadge = {
  critical: 'bg-red-500/20 text-red-400',
  high: 'bg-amber-500/20 text-amber-400',
  medium: 'bg-blue-500/20 text-blue-400',
  low: 'bg-muted text-muted-foreground',
};

const statusBadge = {
  open: 'bg-blue-500/20 text-blue-400',
  'in-progress': 'bg-amber-500/20 text-amber-400',
  overdue: 'bg-red-500/20 text-red-400',
  closed: 'bg-emerald-500/20 text-emerald-400',
};

const ActionHub = () => {
  const [activeTab, setActiveTab] = useState('list');

  const getColumnActions = (colId) =>
    openActions?.filter((a) => a?.status === colId);

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Zap" size={18} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Action Hub
          </h2>
          <p className="text-xs text-muted-foreground">
            Open actions, task board and escalation alerts
          </p>
        </div>

        <div className="ml-auto flex gap-2">
          {['list', 'board', 'escalations'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'list'
                ? 'Open Actions'
                : tab === 'board'
                ? 'Task Board'
                : 'Escalations'}
            </button>
          ))}
        </div>
      </div>

      {/* LIST VIEW */}
      {activeTab === 'list' && (
        <div className="space-y-2">
          {openActions.map((action) => (
            <div
              key={action.id}
              className="flex items-start gap-3 p-3 bg-muted/40 rounded-lg hover:bg-muted/70 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {action.title}
                </p>

                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-xs text-muted-foreground">
                    {action.source}
                  </span>
                  <span className="text-xs text-muted-foreground">·</span>

                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="User" size={10} />
                    {action.owner}
                  </span>

                  <span className="text-xs text-muted-foreground">·</span>

                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Calendar" size={10} />
                    {action.due}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityBadge[action.priority]}`}
                >
                  {action.priority}
                </span>

                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge[action.status]}`}
                >
                  {action.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BOARD VIEW */}
      {activeTab === 'board' && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {boardColumns.map((col) => {
            const colActions = getColumnActions(col.id);

            return (
              <div
                key={col.id}
                className={`${col.bg} border ${col.border} rounded-xl p-3`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className={`text-xs font-semibold uppercase ${col.color}`}
                  >
                    {col.label}
                  </span>

                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full ${col.bg} ${col.color}`}
                  >
                    {colActions.length}
                  </span>
                </div>

                <div className="space-y-2">
                  {colActions.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      No actions
                    </p>
                  )}

                  {colActions.map((a) => (
                    <div
                      key={a.id}
                      className="bg-card border border-border rounded-lg p-2.5"
                    >
                      <p className="text-xs font-medium text-foreground leading-snug mb-1">
                        {a.title}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {a.owner}
                        </span>

                        <span
                          className={`text-xs px-1.5 py-0.5 rounded-full ${priorityBadge[a.priority]}`}
                        >
                          {a.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ESCALATIONS */}
      {activeTab === 'escalations' && (
        <div className="space-y-3">
          {escalationAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`flex items-start gap-4 p-4 rounded-xl border ${
                alert.rag === 'red'
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-amber-500/10 border-amber-500/30'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  alert.rag === 'red'
                    ? 'bg-red-500/20'
                    : 'bg-amber-500/20'
                }`}
              >
                <Icon
                  name="AlertOctagon"
                  size={16}
                  color={alert.rag === 'red' ? '#ef4444' : '#f59e0b'}
                />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  {alert.title}
                </p>

                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">
                    {alert.module}
                  </span>
                  <span className="text-xs text-muted-foreground">·</span>

                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="User" size={10} />
                    Escalate to: {alert.assignee}
                  </span>
                </div>
              </div>

              <button
                className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                  alert.rag === 'red'
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
              >
                Escalate
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionHub;